// ── 연차 내역 섹션 (액션바·모바일카드·데스크탑테이블·페이지네이션) ────────────
import React from 'react';
import { Search } from 'lucide-react';
import { toDateString } from '../../../utils/date';
import AdminColHeader from '../../../components/AdminColHeader';
import { badgeStyle, isAutoGeneratedRow, companyBadgeStyle } from './utils';
import { TYPE_BADGE, CATEGORY_BADGE } from './constants';
// 표·버튼·배지 — 직원 관리 기준 디자인 표준 (2026-08-26 통일)
import {
  stdTableWrap, stdTable, stdTh, stdCell, stdBadgeMuted,
  stdBlackBtn, stdOutlineBtn, stdSearchInput, stdSearchIcon,
} from '../adminlayout/designStandard';
import {
  sectionTitleStyle,
  emptyTdStyle, emptyTextStyle,
  actionBarStyle, actionBtnsStyle,
  detailRowStyle, mobileRowCard,
} from './stylesA';
import {
  countBadgeStyle, correctionBadgeStyle, reasonCellStyle,
  paginationStyle, pageInfoStyle,
  outlineBtn, dangerOutlineBtn,
} from './stylesB';

export function DetailSection({
  isMobile, nameQuery, setNameQuery,
  filteredList, pagedList, currentPage, totalPages, setCurrentPage,
  sheetUrl, syncGoogleSheet, setBulkOpen, openCreate, openEdit, remove,
  hideSheetSync, // 통합 모드 (Phase 1-1d) — 구글시트 동기화 비활성
  showBulk = false,
}) {
  return (
    // minHeight 560 + alignContent start: 여유 공간을 위쪽 고정(자식 간 분산 방지)
    // → 검색 결과 변동에도 검색창/테이블 상단 위치 불변
    // 카드 박스 제거 — 표준(직원 관리)은 표 자체가 카드 (2026-08-26 통일)
    <div style={{ display: 'grid', gap: 8, minHeight: 560, alignContent: 'start' }}>
      <div style={sectionTitleStyle}>연차 내역</div>

      {/* 필터 + 액션 바 */}
      <div style={actionBarStyle(isMobile)}>
        <div style={{ position: 'relative', width: isMobile ? '100%' : 200 }}>
          <Search size={15} style={stdSearchIcon} />
          <input
            style={stdSearchInput}
            value={nameQuery}
            placeholder="이름 검색"
            onChange={(e) => setNameQuery(e.target.value)}
            onFocus={(e) => {
              // AdminLayout의 <main overflow-y-auto>가 input focus 시 자동 scrollIntoView로 점프하는 것 차단
              // (실측: MAIN.scrollTop 0 → 431). focus 직전 scrollTop 저장 후 두 프레임에 걸쳐 복원
              // (브라우저가 두 번에 나눠 스크롤 조정하는 경우 대비)
              const main = e.target.closest('main');
              if (!main) return;
              const saved = main.scrollTop;
              requestAnimationFrame(() => {
                if (main.scrollTop !== saved) main.scrollTop = saved;
                requestAnimationFrame(() => {
                  if (main.scrollTop !== saved) main.scrollTop = saved;
                });
              });
            }}
          />
        </div>
        <div style={actionBtnsStyle(isMobile)}>
          {!hideSheetSync && (
            <>
              <button style={stdOutlineBtn} onClick={syncGoogleSheet}>구글시트 동기화</button>
              <button style={stdOutlineBtn} disabled={!sheetUrl} onClick={() => window.open(sheetUrl, '_blank')}>구글시트 열기</button>
            </>
          )}
          {showBulk && <button style={stdOutlineBtn} onClick={() => setBulkOpen(true)}>대량 추가</button>}
          <button style={stdBlackBtn} onClick={openCreate}>+ 연차 추가</button>
        </div>
      </div>

      {isMobile ? (
        // ── 모바일: 컴팩트 1줄 카드 (탭 → 수정 모달, 삭제는 모달 내부) ──────
        <div style={{ display: 'grid', gap: 4 }}>
          {filteredList.length === 0 && (
            <div style={emptyTextStyle}>데이터가 없습니다.</div>
          )}
          {pagedList.map((row) => {
            const isGenerated = isAutoGeneratedRow(row);
            const isPaidRecognized = row.payroll_effect === 'paid_cover' && row.leave_balance_effect === 'none';
            const dateShort = String(toDateString(row.date) || '').slice(5); // MM-DD
            return (
              <article
                key={`m-${row.id}`}
                onClick={() => openEdit(row)}
                style={{
                  ...mobileRowCard(row.is_correction, isGenerated),
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 12px', cursor: 'pointer',
                }}
              >
                {/* 이름 - 고정 폭 70 */}
                <span style={{ width: 70, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.name}
                </span>
                {/* 회사 뱃지 자리 - 고정 폭 40 (없어도 공간 유지) */}
                <span style={{ width: 40, display: 'flex', alignItems: 'center' }}>
                  {row.company_name && <span style={companyBadgeStyle(row.company_name)}>{row.company_name}</span>}
                </span>
                {/* 날짜 - 고정 폭 50 */}
                <span style={{ width: 50, fontSize: 12, color: 'var(--text-sub)' }}>{dateShort}</span>
                {/* 타입 뱃지 - 고정 폭 60 */}
                <span style={{ width: 60, display: 'flex', alignItems: 'center' }}>
                  <span style={badgeStyle(TYPE_BADGE, isPaidRecognized ? '유급인정' : row.type)}>
                    {isPaidRecognized ? '유급인정' : row.type}
                  </span>
                </span>
                {/* 개수 - 고정 폭 30, 왼쪽 정렬 */}
                <span style={{ width: 30, fontSize: 13, fontWeight: 500 }}>{row.count}</span>
                {/* 화살표 - 오른쪽 끝 */}
                <span style={{ marginLeft: 'auto', fontSize: 16, color: 'var(--text-muted)' }}>›</span>
              </article>
            );
          })}
        </div>
      ) : (
        // ── 데스크탑: 테이블 ────────────────────────────────────────────────
        // minHeight: 검색 시 행 수 변경에도 컨테이너 높이 고정 (페이지네이션 위치 안정)
        <div style={{ ...stdTableWrap, minHeight: 290 }}>
          <table style={{ ...stdTable, tableLayout: 'fixed', minWidth: 1100 }}>
            <colgroup>
              <col style={{ width: 90 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 72 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 80 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                {/* 개수·정정 컬럼은 데이터 셀과 맞춰 가운데 정렬 */}
                {[
                  { label: '성명',      align: 'left',   db: 'leaves.user_id → users.name' },
                  { label: '연차사용일', align: 'left',   db: 'leaves.date' },
                  { label: '종류',      align: 'left',   db: 'leaves.type' },
                  { label: '구분',      align: 'left',   db: 'leaves.category' },
                  { label: '개수',      align: 'center', db: 'leaves.count' },
                  { label: '사유',      align: 'left',   db: 'leaves.reason' },
                  { label: '정정',      align: 'center', db: 'leaves.is_correction' },
                  { label: '등록일',    align: 'left',   db: 'leaves.created_at' },
                  { label: '관리',      align: 'left' },
                ].map(({ label, align, db }) => (
                  <th key={label} style={{ ...stdTh, textAlign: align }}>
                    <AdminColHeader label={label} db={db} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredList.length === 0 && (
                <tr><td colSpan="9" style={emptyTdStyle}>데이터가 없습니다.</td></tr>
              )}
              {pagedList.map((row, rowIndex) => {
                const isGenerated = isAutoGeneratedRow(row);
                const isPaidRecognized = row.payroll_effect === 'paid_cover' && row.leave_balance_effect === 'none';
                const cellStyle = stdCell(rowIndex, pagedList.length); // 마지막 행 밑줄 제거 (표준)
                return (
                  <tr key={row.id} style={detailRowStyle(row.is_correction, isGenerated)}>
                    <td style={cellStyle}>
                      <strong>{row.name}</strong>
                      {/* 통합 표시 시 외부 회사 배지 */}
                      {row.company_name && (
                        <span style={{ ...companyBadgeStyle(row.company_name), marginLeft: 5 }}>{row.company_name}</span>
                      )}
                    </td>
                    <td style={cellStyle}>{toDateString(row.date)}</td>
                    <td style={cellStyle}>
                      <span style={badgeStyle(TYPE_BADGE, row.type)}>{row.type}</span>
                      {isPaidRecognized && (
                        <span style={{ ...badgeStyle(TYPE_BADGE, '유급인정'), marginLeft: 6 }}>유급인정</span>
                      )}
                    </td>
                    <td style={cellStyle}>
                      <span style={badgeStyle(CATEGORY_BADGE, row.category)}>{row.category}</span>
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                      <span style={countBadgeStyle}>{row.count}</span>
                    </td>
                    <td style={{ ...cellStyle, ...reasonCellStyle }} title={row.reason || ''}>
                      {row.reason || <span style={{ color: 'var(--border)' }}>-</span>}
                    </td>
                    <td style={{ ...cellStyle, textAlign: 'center' }}>
                      {row.is_correction
                        ? <span style={correctionBadgeStyle}>정정됨</span>
                        : <span style={stdBadgeMuted}>정상</span>}
                    </td>
                    <td style={cellStyle}>{toDateString(row.created_at)}</td>
                    <td style={cellStyle}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={outlineBtn} onClick={() => openEdit(row)}>수정</button>
                        <button style={dangerOutlineBtn} onClick={() => remove(row)}>삭제</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 페이지네이션 — 검색 0건일 때 visibility:hidden으로 공간 유지 (레이아웃 흔들림 방지) */}
      <div style={{ ...paginationStyle, visibility: filteredList.length > 0 ? 'visible' : 'hidden' }}>
        <button
          style={stdOutlineBtn}
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        >
          이전
        </button>
        <span style={pageInfoStyle}>{currentPage} / {totalPages}</span>
        <button
          style={stdOutlineBtn}
          disabled={currentPage >= totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          다음
        </button>
      </div>
    </div>
  );
}
